import FileSaver from "file-saver";
import { toast } from "react-toastify";
import {
  FormDataConverter,
  FormSchema,
  isFileInputElement,
} from "@graffer-inc/form-schema";
import {
  ApplicationEventType,
  TenantProcessResultDocument,
} from "@graffer-inc/cloud-gov-core";

import { TabUiState } from "./tab-ui-state";
import apiClient from "../../../apis/api-client";
import {
  BaseApplicationService,
  SmartApplyApiError,
  reportUnexpectedError,
  nameCsvFileWithResourceId,
  namePdfOutputFile,
  FileType,
  generateZip,
} from "@graffer-inc/smart-apply";
import {
  ProcedureEntity,
  ProcedureType,
} from "../../../domain/procedures/procedure-entity";
import {
  ModifiedApplicationContentType,
  ApplicationEntity,
  ApplyStatus,
  ModifyInfoType,
  UploadIssueFile,
} from "../../../domain/application/application-entity";
import { CalcRequestType } from "../../../domain/payment/calc-request";
import { ApplicationHistory } from "../../../domain/application/application-history";
import { rebuildQuery } from "../../../../utils/url-utils";
import { RefundRequest } from "../../../domain/payment/refund-request";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { ApplicationMessageJSON } from "../../../domain/application/application-message";
import { ApplicationEventFactory } from "../../../domain/application/application-event-factory";
import {
  PaymentDetailByPaymentJSON,
  PaymentSummaryJSON,
} from "../../../domain/payment/payment-summary";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { ApiLoadingState } from "../../../domain/api-loading-state";
import { ApplicationHistoryFactory } from "../../../domain/application/application-history-factory";
import { PdfRenderError } from "../../../domain/pdf-error";
import { CSVEncoding } from "../../../domain/file-encoding";
import Analytics from "../../../../common/analytics";
import { ApplicationInternalCommentJSON } from "../../../domain/application/application-internal-comment";
import { fetchAttachedFileWithRetry } from "../../../apis/files/base";

type PageLoadingState = {
  isLoading: boolean;
  isPDFOutputting: boolean;
  modifyingProcess: ApiLoadingState;
  isBulkDownloading: boolean;
};

export type ApplicationDetailContextProps = {
  loadingStates: PageLoadingState;
  tabState: TabUiState;
  isInitialized: () => boolean;
  application?: ApplicationEntity;
  procedure?: ProcedureEntity;
  histories: GrafferGqlResult<ApplicationHistory[]>;
  events: GrafferGqlResult<ApplicationEventType[]>;
  messages: GrafferGqlResult<ApplicationMessageJSON[]>;
  internalComments: GrafferGqlResult<ApplicationInternalCommentJSON[]>;
  paymentSummary: GrafferGqlResult<PaymentSummaryJSON>;
  paymentDetailsByPayment: GrafferGqlResult<PaymentDetailByPaymentJSON[]>;
  selectedMetaIds: Array<string>;
  csvEncoding: CSVEncoding;
  includeTenantProcessResult: boolean;
  requireSelectableRawValue: boolean;

  setTabUIState: typeof ApplicationDetailService.prototype.setTabUIState;

  onFileIssued: typeof ApplicationDetailService.prototype.onFileIssued;
  onIssueFileDeleted: typeof ApplicationDetailService.prototype.onIssueFileDeleted;
  onStatusUpdated: typeof ApplicationDetailService.prototype.onStatusUpdated;
  onModify: typeof ApplicationDetailService.prototype.onModify;
  onModificationEnd: typeof ApplicationDetailService.prototype.onModificationEnd;
  onAddPayment: typeof ApplicationDetailService.prototype.onAddPayment;
  onPageMount: typeof ApplicationDetailService.prototype.onPageMount;
  onWithdrawPostPayment: typeof ApplicationDetailService.prototype.onWithdrawPostPayment;
  onTabChange: typeof ApplicationDetailService.prototype.onTabChange;
  onBulkDownload: typeof ApplicationDetailService.prototype.onBulkDownload;

  onPDFOutputRequested: typeof ApplicationDetailService.prototype.onPDFOutputRequested;
  onPDFOutputForCustomerRequested: typeof ApplicationDetailService.prototype.onPDFOutputForCustomerRequested;
  refundV2: typeof ApplicationDetailService.prototype.refundV2;
  adjustExceedingOrDeficiency: typeof ApplicationDetailService.prototype.adjustExceedingOrDeficiency;

  toggleCsvMetaCheck: typeof ApplicationDetailService.prototype.toggleCsvMetaCheck;
  toggleAllCsvMetaCheck: typeof ApplicationDetailService.prototype.toggleAllCsvMetaCheck;
  downloadSingleRecordCSV: typeof ApplicationDetailService.prototype.downloadSingleRecordCSV;
  onChangeCSVEncoding: typeof ApplicationDetailService.prototype.onChangeCSVEncoding;
  onCheckIncludeTenantProcessResult: typeof ApplicationDetailService.prototype.onCheckIncludeTenantProcessResult;
  onChangeRequireSelectableRawValue: typeof ApplicationDetailService.prototype.onChangeRequireSelectableRawValue;
  onUpdateTenantProcessResult: typeof ApplicationDetailService.prototype.onUpdateTenantProcessResult;
  onRevertStatusToWip: typeof ApplicationDetailService.prototype.onRevertStatusToWip;
};

export class ApplicationDetailService extends BaseApplicationService<ApplicationDetailContextProps> {
  state = {
    // states
    loadingStates: {
      isLoading: false,
      isPDFOutputting: false,
      modifyingProcess: {
        state: "idle" as const,
      },
      isBulkDownloading: false,
    },
    tabState: TabUiState.initialize(),

    setTabUIState: this.setTabUIState.bind(this),
    application: undefined as ApplicationEntity | undefined,
    procedure: undefined as ProcedureEntity | undefined,
    histories: GrafferGqlResult.init(),
    events: GrafferGqlResult.init(),
    messages: GrafferGqlResult.init(),
    internalComments: GrafferGqlResult.init(),
    paymentSummary: GrafferGqlResult.init(),
    paymentDetailsByPayment: GrafferGqlResult.init(),
    selectedMetaIds: [] as Array<string>,
    csvEncoding: "Shift_JIS" as CSVEncoding,
    includeTenantProcessResult: false,
    requireSelectableRawValue: false,
    // functions
    isInitialized: () => {
      return !!(this.state.application && this.state.procedure);
    },
    onFileIssued: this.onFileIssued.bind(this),
    onIssueFileDeleted: this.onIssueFileDeleted.bind(this),
    onTabChange: this.onTabChange.bind(this),
    onPageMount: this.onPageMount.bind(this),
    onStatusUpdated: this.onStatusUpdated.bind(this),
    onPDFOutputRequested: this.onPDFOutputRequested.bind(this),
    onPDFOutputForCustomerRequested:
      this.onPDFOutputForCustomerRequested.bind(this),
    onModify: this.onModify.bind(this),
    onModificationEnd: this.onModificationEnd.bind(this),
    onAddPayment: this.onAddPayment.bind(this),
    onWithdrawPostPayment: this.onWithdrawPostPayment.bind(this),
    onBulkDownload: this.onBulkDownload.bind(this),
    refundV2: this.refundV2.bind(this),
    adjustExceedingOrDeficiency: this.adjustExceedingOrDeficiency.bind(this),
    toggleCsvMetaCheck: this.toggleCsvMetaCheck.bind(this),
    toggleAllCsvMetaCheck: this.toggleAllCsvMetaCheck.bind(this),
    downloadSingleRecordCSV: this.downloadSingleRecordCSV.bind(this),
    onChangeCSVEncoding: this.onChangeCSVEncoding.bind(this),
    onCheckIncludeTenantProcessResult:
      this.onCheckIncludeTenantProcessResult.bind(this),
    onChangeRequireSelectableRawValue:
      this.onChangeRequireSelectableRawValue.bind(this),
    onUpdateTenantProcessResult: this.onUpdateTenantProcessResult.bind(this),
    onRevertStatusToWip: this.onRevertStatusToWip.bind(this),
  };

  setTabUIState(tabState: TabUiState) {
    if (!this.state.tabState.equals(tabState.getValue())) {
      this.setState({
        tabState,
      });
      this.history.push(rebuildQuery({ tab: tabState.getValue() }));
    }
  }

  onTabChange(tab?: string) {
    if (TabUiState.isValid(tab)) {
      this.setState({
        tabState: new TabUiState(tab),
      });
    }
  }

  async onPageMount(id: string) {
    this.setLoadingState({ isLoading: true });
    try {
      const {
        application,
        proc,
        histories,
        eventResults,
        messagesResults,
        internalCommentResults,
        paymentSummary,
        paymentDetailsByPayment,
      } = await this.fetchData(id);
      this.setState({
        application: application,
        histories: histories,
        events: eventResults,
        messages: messagesResults,
        internalComments: internalCommentResults,
        paymentSummary: paymentSummary,
        paymentDetailsByPayment: paymentDetailsByPayment,
        procedure: proc,
        selectedMetaIds: proc
          .allCSVMetaField()
          .filter((v) => v.default)
          .map((v) => v.id),
      });
    } catch (e) {
      if (SmartApplyApiError.is404(e)) {
        // TODO: render 404.
        reportUnexpectedError(e);
        return;
      }
      if (SmartApplyApiError.isErrorCode(e, "unauthorized")) {
        SmartApplyApiError.handle401Error();
        return;
      }
      reportUnexpectedError(e);
    } finally {
      this.setLoadingState({ isLoading: false });
    }
  }

  async onAddPayment(req: CalcRequestType) {
    try {
      const { data } = await apiClient.addPaymentToApplication(
        this.state.application!.id,
        {
          ...req,
          version: this.application.version,
        },
      );
      this.setState({
        application: data,
      });
      toast.success("決済を登録しました。");
      return true;
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      if (this.handleRetryableError(apiError)) {
        return false;
      }
      if (
        SmartApplyApiError.isErrorCode(
          apiError,
          "bad_request.payment_amount_is_less_than_minimum",
        )
      ) {
        toast.error("支払いは2円以上の金額で依頼してください。", {
          autoClose: 8000,
        });
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async onWithdrawPostPayment(paymentIndex: number) {
    try {
      const { data } = await apiClient.withdrawnPostPayment(
        this.state.application!.id,
        {
          paymentIndex: paymentIndex,
          version: this.application.version,
        },
      );
      this.setState({
        application: data,
      });
      toast.success("決済を取下げました。");
      return true;
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async refundV2(index: number, req: RefundRequest, message?: string) {
    try {
      const { data } = await apiClient.refundPaymentsV2(
        this.state.application!.id,
        req.toAPI({
          paymentIndex: index,
          version: this.state.application!.version,
          message: message,
        }),
      );
      const { paymentSummary, paymentDetailsByPayment } = await this.fetchData(
        data.id,
      );
      this.setState({
        application: data,
        paymentSummary: paymentSummary,
        paymentDetailsByPayment: paymentDetailsByPayment,
      });
      toast.success("返金しました。");
      return true;
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async adjustExceedingOrDeficiency(paymentIndex: number): Promise<boolean> {
    try {
      const result = await runGqlQueryV2(() =>
        gqlClient.AdjustPaymentExceedingOrDeficiency(
          {
            applicationId: this.state.application!.id,
            version: this.state.application!.version.valueOf(),
            paymentIndex: paymentIndex,
          },
          gqlHeader("m"),
        ),
      );
      if (result.data?.adjustPaymentExceedingOrDeficiency) {
        toast.success("登録に成功しました。");
        await this.onPageMount(this.state.application!.id);
        return true;
      } else {
        toast.error("登録に失敗しました。");
        return false;
      }
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async onStatusUpdated(
    nextStatus: ApplyStatus,
    skipNotification: boolean,
    message?: string,
  ) {
    const { data: updated } = await apiClient.updateApplicationStatus(
      this.application.id,
      {
        status: nextStatus,
        skipNotification: skipNotification,
        version: this.application.version,
        message: message,
      },
    );
    this.setState({
      application: updated,
    });
  }

  async onPDFOutputRequested() {
    await this.outputPDF(
      () => apiClient.outputPDF(this.application.id),
      namePdfOutputFile(this.application.id),
    );
  }

  async onPDFOutputForCustomerRequested() {
    await this.outputPDF(
      () => apiClient.outputPDFForCustomer(this.application.id),
      this.state.procedure?.customerDlPdfFileName(),
    );
  }

  private async outputPDF(
    apiCaller: () => ReturnType<typeof apiClient.outputPDF>,
    saveFileName?: string,
  ) {
    try {
      this.setLoadingState({
        isPDFOutputting: true,
      });
      const { data } = await apiCaller();
      FileSaver.saveAs(new Blob([data]), saveFileName);
      Analytics.downloadApplicationPdf();
      toast.success("PDFをダウンロードしました。");
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      if (this.handleRetryableError(apiError)) {
        return;
      }
      if (
        SmartApplyApiError.isErrorCode(
          apiError,
          "applications.pdf_field_render_failure",
        )
      ) {
        const errorMsg = new PdfRenderError(apiError).message(
          this.state.procedure!.published!,
        );
        if (errorMsg) {
          toast.error(
            errorMsg,
            { autoClose: 8000 }, // TODO use UI that does not vanish(e,g, banner)
          );
          return;
        }
      }
      reportUnexpectedError(apiError);
      toast.error(
        "PDFダウンロードに失敗しました。管理者にお問い合わせください。",
      );
    } finally {
      this.setLoadingState({
        isPDFOutputting: false,
      });
    }
  }

  async onFileIssued(uploadFile: UploadIssueFile, skipNotification: boolean) {
    this.setLoadingState({ isLoading: true });
    try {
      this.setLoadingState({ isLoading: true });
      await apiClient.addIssueFile(
        this.application.id,
        {
          file: uploadFile,
          version: this.application.version,
        },
        skipNotification,
      );
      await this.onPageMount(this.application.id);
      toast.success("交付物をアップロードしました。");
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "bad_request.version_outdated")) {
        toast.error(
          "申請情報の更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      toast.error(
        "交付物のアップロードに失敗しました。お手数ですが管理者にお問い合わせください。",
      );
      reportUnexpectedError(e);
    } finally {
      this.setLoadingState({ isLoading: false });
    }
  }

  async onIssueFileDeleted(index: number) {
    this.setLoadingState({ isLoading: true });
    try {
      await apiClient.deleteIssueFile({
        id: this.application.id,
        index,
        version: this.application.version,
      });
      await this.onPageMount(this.application.id);
      toast.success("交付物を削除しました。");
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "bad_request.version_outdated")) {
        toast.error(
          "申請情報の更新に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      toast.error(
        "交付物の削除に失敗しました。お手数ですが管理者にお問い合わせください。",
      );
      reportUnexpectedError(e);
    }
  }

  async onModify(
    content: ModifiedApplicationContentType,
    modifyInfo: ModifyInfoType,
  ) {
    this.setLoadingState({
      modifyingProcess: {
        state: "loading",
      },
    });
    try {
      await apiClient.modifyApplication(this.application.id, {
        ...content,
        ...modifyInfo,
        version: this.application.version,
      });
      this.setLoadingState({ modifyingProcess: { state: "success" } });
      toast.success("申請内容を訂正しました");
      await this.onPageMount(this.application.id);
    } catch (e) {
      const setFailure = (message: string) => {
        toast.error(message);
        this.setLoadingState({
          modifyingProcess: {
            state: "failure",
            errorMessage: message,
          },
        });
      };
      if (SmartApplyApiError.isRetryable(e)) {
        setFailure(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
        return;
      }
      if (SmartApplyApiError.isErrorCode(e, "bad_request.version_outdated")) {
        setFailure(
          "申請内容の訂正に失敗しました。画面を更新して再度実行してください。",
        );
        return;
      }
      // 個別開発手続きのバリデーションは申請時とは違い訂正時はサーバーのみで行うため、
      // エラー内容のレスポンスをユーザーに表示する。
      if (
        !!this.state.procedure?.customTag &&
        SmartApplyApiError.isBadRequest(e)
      ) {
        setFailure(
          "訂正内容に入力制約違反があるため訂正に失敗しました。本手続きには特別な入力制約がかけられています。手続きの管理者に入力制約内容を確認し、訂正内容を修正してください。",
        );
        return;
      }
      setFailure(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      reportUnexpectedError(e);
    }
  }

  toggleCsvMetaCheck(id: string) {
    const { selectedMetaIds } = this.state;
    this.setState({
      selectedMetaIds: selectedMetaIds.includes(id)
        ? selectedMetaIds.filter((m) => m !== id)
        : [...selectedMetaIds, id],
    });
  }

  toggleAllCsvMetaCheck(checked: boolean) {
    this.setState({
      selectedMetaIds: checked
        ? this.state.procedure!.allCSVMetaField().map((m) => m.id)
        : [],
    });
  }

  async downloadSingleRecordCSV() {
    if (!this.state.application || !this.state.procedure) {
      toast.error("CSVダウンロードでエラーが発生しました");
      return;
    }
    const allApplicationMetaId = this.state.procedure
      .allCSVMetaField()
      .map((m) => m.id);
    const applicationMetaIds = allApplicationMetaId.filter((id) =>
      this.state.selectedMetaIds.includes(id),
    );
    try {
      const { data } = await apiClient.downloadApplicationCSV(
        this.state.application.id,
        applicationMetaIds,
        this.state.csvEncoding,
        {
          includeTenantProcessResult: this.state.includeTenantProcessResult,
          requireSelectableRawValue: this.state.requireSelectableRawValue,
        },
      );
      FileSaver.saveAs(
        data,
        nameCsvFileWithResourceId(
          this.state.procedure.name,
          this.state.application.id,
        ),
      );
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      switch (apiError.errorCode()) {
        case "applications.cert_verification_pending":
          toast.error(
            "電子署名が検証中の申請があります。しばらく時間を置いて再度ダウンロードをしてください。",
          );
          return;
        default:
          reportUnexpectedError(e);
          toast.error("CSVダウンロードでエラーが発生しました");
          return;
      }
    }
  }

  onModificationEnd() {
    this.setLoadingState({
      modifyingProcess: {
        state: "idle",
      },
    });
  }

  async onBulkDownload() {
    this.setLoadingState({ isBulkDownloading: true });
    const schema = FormSchema.fromJSON(
      this.state.procedure!.published!.template.formSchema,
    );
    const data = new FormDataConverter(
      schema,
      this.application.jsonDocument,
    ).flattenWithElement();
    const fileElements = Object.keys(data)
      .map((key) => data[key])
      .filter((elem) => isFileInputElement(elem.element));

    try {
      const files: FileType[] = [];
      for (let i = 0; i < this.application.files.length; i++) {
        const file = this.application.files[i];
        const uri = this.application.formatFileResourcesURLWithIndex(i);
        const elem = fileElements.find((v) => v.value === file.refKey);
        if (!elem) {
          // アップロード後にファイル項目が非表示になった場合など
          continue;
        }
        const name =
          elem.index !== undefined
            ? `${elem.element.label.text}[${elem.index}]_${file.name}`
            : `${elem.element.label.text}_${file.name}`;
        const res = await fetchAttachedFileWithRetry(() =>
          apiClient.fetchAttachedFile(uri),
        );
        files.push({ name, blob: res.data });
      }
      if (files.length === 0) {
        // 対象の申請に1件も添付ファイルがない場合はエラーメッセージを表示する。
        // 本来、申請情報に含まれない添付ファイルはapplications.filesに保存しないことが望ましいが、
        // 空のzipがダウンロードされないよう暫定対応をしている。
        toast.error("申請内容に添付ファイルがありません。");
        return;
      }
      const zip = await generateZip(files, { encodeShiftJis: true });
      FileSaver.saveAs(zip, `${this.application.id}.zip`);
      Analytics.downloadApplicationAttachedFiles();
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return;
      }
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      reportUnexpectedError(e);
    } finally {
      this.setLoadingState({ isBulkDownloading: false });
    }
  }

  onChangeCSVEncoding(encoding: CSVEncoding) {
    this.setState({ csvEncoding: encoding });
  }

  onCheckIncludeTenantProcessResult(checked: boolean) {
    this.setState({ includeTenantProcessResult: checked });
  }

  onChangeRequireSelectableRawValue(isRequired: boolean) {
    this.setState({ requireSelectableRawValue: isRequired });
  }

  async onUpdateTenantProcessResult(
    document: TenantProcessResultDocument,
  ): Promise<boolean> {
    const res = await runGqlQueryV2(() =>
      gqlClient.UpdateTenantProcessResult(
        {
          applicationId: this.application.id,
          document,
        },
        gqlHeader("m"),
      ),
    );
    if (res.error) {
      if (SmartApplyApiError.is409(res.error.rawError)) {
        toast.error(
          "管理情報の更新に失敗しました。画面を更新してから再度管理情報を更新してください。",
        );
        return false;
      }
      reportUnexpectedError(res.error.rawError);
      toast.error("管理情報の更新に失敗しました。");
      return false;
    }
    toast.success("管理情報を更新しました。");
    await this.onPageMount(this.application.id);
    return true;
  }

  async onRevertStatusToWip(internalComment: string) {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.RevertStatus(
          {
            applicationId: this.application.id,
            revertStatusCommand: {
              status: "WIP",
              version: this.application.version.valueOf(),
              internalComment,
            },
          },
          gqlHeader("m"),
        ),
      );
      if (res.error) {
        throw res.error.rawError;
      }
      switch (res.data?.revertStatus.__typename) {
        case "IsOutdatedAppVersion":
          toast.error(
            "ステータスの更新に失敗しました。画面を更新して再度実行してください。",
          );
          break;
        case "ProcedureAplsReachedQuota":
          toast.error("定員に達したため、ステータス更新できません。");
          break;
        case "RevertStatusResult":
          toast.success("ステータスを更新しました。");
          await this.onPageMount(this.state.application!.id);
          break;
        default:
          throw new Error("failed to revert status");
      }
    } catch (e) {
      toast.error("ステータスの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }

  private setLoadingState(state: Partial<PageLoadingState>) {
    this.setState({
      loadingStates: {
        ...this.state.loadingStates,
        ...state,
      },
    });
  }

  private async fetchData(id: string) {
    // MEMO(yo-tak): application も applicationAllDetailData のレスポンスに含まれるが、ここでは利用しない。
    // レスポンスの支払い情報に含まれる InvoiceItemType の値が apiClient と gqlClient で異なるため、
    // コンポーネントとその利用箇所を調査、修正してからでないと切り替えられない。
    const { data: application } = await apiClient.fetchApplication(id);
    const aplDetailData = await runGqlQueryV2(() =>
      gqlClient.applicationAllDetailData(
        { id: id, typedId: id },
        gqlHeader("q"),
      ),
    );
    if (aplDetailData.error) {
      throw aplDetailData.error;
    }

    const proc = ProcedureEntity.fromJSON(
      aplDetailData.data!.applicationDetailData.procedure as ProcedureType,
    );
    const histories = aplDetailData.convert((v) =>
      v.applicationHistories.map(ApplicationHistoryFactory.createFromGql),
    );
    // TODO:: error handling
    let eventResults: GrafferGqlResult<ApplicationEventType[]> =
      GrafferGqlResult.init();
    let messagesResults: GrafferGqlResult<ApplicationMessageJSON[]> =
      GrafferGqlResult.init();
    let internalCommentResults: GrafferGqlResult<
      ApplicationInternalCommentJSON[]
    > = GrafferGqlResult.init();

    eventResults = aplDetailData.convert((v) =>
      v.applicationEventsAndMessages.events.map(
        ApplicationEventFactory.createFromGql,
      ),
    );
    messagesResults = aplDetailData.convert((v) =>
      v.applicationEventsAndMessages.messages.map(
        (m) => m as ApplicationMessageJSON,
      ),
    );
    internalCommentResults = aplDetailData.convert((v) =>
      v.internalCommentsAndUsers.map(
        (ic) => ic as ApplicationInternalCommentJSON,
      ),
    );

    let paymentSummary: GrafferGqlResult<PaymentSummaryJSON> =
      GrafferGqlResult.init();
    let paymentDetailsByPayment: GrafferGqlResult<
      PaymentDetailByPaymentJSON[]
    > = GrafferGqlResult.init();
    paymentSummary = aplDetailData.convert(
      (v) => v.paymentSummary as PaymentSummaryJSON,
    );
    paymentDetailsByPayment = aplDetailData.convert(
      (v) => v.paymentDetailByPayments as PaymentDetailByPaymentJSON[],
    );

    return {
      application,
      proc,
      histories,
      eventResults,
      messagesResults,
      internalCommentResults,
      paymentSummary,
      paymentDetailsByPayment,
    };
  }

  // accessors
  private get application() {
    return this.state.application!;
  }
}
